import { NotPorted } from './NotPorted';
import type { MapEngine } from '@mighty-map/map-engine';
import { Component, lazy, Suspense, useMemo, type ComponentType, type ErrorInfo, type ReactNode } from 'react';
import { LineIcon, GLYPHS } from '../shell/icons';
import type { ControllerTool, ToolProps } from './registry';

class ToolBoundary extends Component<{ label: string; children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };
  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }
  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    console.error(`[tools] ${this.props.label} failed`, error, info.componentStack);
  }
  override render(): ReactNode {
    if (this.state.failed) {
      return (
        <div className="mm-not-ported" role="alert">
          <h3>{this.props.label} could not open</h3>
          <p>Close the pane and try again. If it keeps happening, reload the page.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

/** The tool pane: production's green header (title + close), the tool inside, the project logo pinned below. */
export function ToolPanel({
  tool,
  engine,
  logoUrl,
  onClose,
}: {
  tool: ControllerTool;
  engine: MapEngine | null;
  logoUrl: string | null;
  onClose: () => void;
}) {
  const Lazy = useMemo<ComponentType<ToolProps> | null>(() => (tool.load ? lazy(tool.load) : null), [tool]);
  const titleId = `mm-panel-title-${tool.id}`;
  return (
    <section className="mm-panel controller-panel" role="dialog" aria-labelledby={titleId} data-tool={tool.id}>
      <div className="mm-panel-header">
        <h2 id={titleId}>{tool.label}</h2>
        <button
          type="button"
          className="mm-panel-close"
          title="Close"
          aria-label={`Close ${tool.label}`}
          onClick={onClose}
        >
          <LineIcon size={18} stroke={2.2}>
            {GLYPHS.close}
          </LineIcon>
        </button>
      </div>
      <div className="mm-panel-body">
        {Lazy ? (
          <ToolBoundary label={tool.label}>
            <Suspense fallback={<div className="mm-panel-loading">Opening {tool.label}…</div>}>
              <Lazy engine={engine} close={onClose} />
            </Suspense>
          </ToolBoundary>
        ) : (
          <NotPorted tool={tool} />
        )}
      </div>
      {logoUrl && (
        <div className="mm-panel-logo" aria-hidden="true">
          <img src={logoUrl} alt="" />
        </div>
      )}
    </section>
  );
}
